/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

/**
 * Smoothly scrolls the section with the matching `id` into view, aligning its
 * top with the viewport top (offset by each section's `scroll-margin-top`,
 * which accounts for the fixed `NavigationBar`).
 *
 * Used by every nav button that maps to an in-page section, so the behaviour
 * stays consistent whether the user clicks "About", "Connectors", "CLI", etc.
 */
const scrollToSection =
	(sectionId: string): (() => void) =>
	() => {
		getDocument()?.getElementById(sectionId)?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	};

const styles = cssMap({
	centerNav: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		justifySelf: 'center',
		marginBottom: token('space.negative.050'),
		'@media (max-width: 48rem)': {
			display: 'none',
		},
	},
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: 'inherit',
		font: 'inherit',
		cursor: 'pointer',
	},
});

type NavButtonsProps = {
	ariaLabel: string;
};

export const NavButtons = ({ ariaLabel }: NavButtonsProps): JSX.Element => {
	// CLI and MCP both scroll to the new "Connect your agents" section,
	// which renders the CLI and MCP options side-by-side. There's no tab
	// state to flip on the new section, so a plain scroll is enough.
	return (
		<nav css={styles.centerNav} aria-label={ariaLabel}>
			<button type="button" css={styles.button} onClick={scrollToSection('overview')}>
				<FormattedMessage {...messages.navAbout} />
			</button>
			<button type="button" css={styles.button} onClick={scrollToSection('connectors')}>
				<FormattedMessage {...messages.navConnectors} />
			</button>
			<button type="button" css={styles.button} onClick={scrollToSection('getting-started-v2')}>
				<FormattedMessage {...messages.navCliFirst} />
			</button>
			<button type="button" css={styles.button} onClick={scrollToSection('getting-started-v2')}>
				<FormattedMessage {...messages.navMcp} />
			</button>
			{/* TODO: GRAPHYTE-2511 — re-add the "Docs" nav item once an in-page
			    docs section / external destination exists. */}
		</nav>
	);
};
